// Auto-generated lightweight class for ActionParameter
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { ActionParameterReadAPI, ActionParameterWriteAPI, ActionParameterReadAPISchema, ActionParameterWriteAPISchema } from '../types/actionparameter';
import { ModelRegistry } from '../../registries/model-registry';
import { ActionBindsParameter, ActionParameterTypedBy, AtScope, HasAttribute, HasAttributeValue, HasDerivedValue, Mentions } from '../../relationships/relationship';

import { Item } from './item';
import { Action } from './action';
import { SecurityScope } from '../../shared/api/securityscope';
import { Attribute } from './attribute';
import { AttributeValue } from './attributevalue';
import { DerivedValue } from './derivedvalue';
import { Expression } from './expression';



export interface ActionParameter extends Partial<ActionParameterReadAPI> {}
export class ActionParameter extends NeoNodeBase<ActionParameterReadAPI, ActionParameterWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "ActionParameter";
  static readonly readSchema = ActionParameterReadAPISchema;
  static readonly writeSchema = ActionParameterWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    attributes: { target: "Attribute", cardinality: "many", isParent: false, relClass: "HasAttribute", direction: "out" },
    attribute_values: { target: "AttributeValue", cardinality: "many", isParent: false, relClass: "HasAttributeValue", direction: "out" },
    derived_values: { target: "DerivedValue", cardinality: "many", isParent: false, relClass: "HasDerivedValue", direction: "out" },
    mentioned_in_expressions: { target: "Expression", cardinality: "many", isParent: false, relClass: "Mentions", direction: "in" },
    item: { target: "Item", cardinality: "one", isParent: false, relClass: "ActionParameterTypedBy", direction: "out" },
    bound_to: { target: "Action", cardinality: "many", isParent: false, relClass: "ActionBindsParameter", direction: "in" },
  };

  protected get modelName(): string {
    return 'ActionParameter';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return ActionParameter.label;
  }


  constructor(data: Partial<ActionParameterWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: ActionParameterReadAPI, loadContext?: Record<string, any>): ActionParameter {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { item, bound_to, security_scope, attributes, attribute_values, derived_values, mentioned_in_expressions, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = ActionParameterReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, item: item, bound_to: bound_to, security_scope: security_scope, attributes: attributes, attribute_values: attribute_values, derived_values: derived_values, mentioned_in_expressions: mentioned_in_expressions };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return ActionParameter.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadOneRelationship(processed, 'item', 'Item', ModelRegistry);
    loadManyRelationship(processed, 'bound_to', 'Action', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadManyRelationship(processed, 'attributes', 'Attribute', ModelRegistry);
    loadManyRelationship(processed, 'attribute_values', 'AttributeValue', ModelRegistry);
    loadManyRelationship(processed, 'derived_values', 'DerivedValue', ModelRegistry);
    loadManyRelationship(processed, 'mentioned_in_expressions', 'Expression', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new ActionParameter(processed as any);

    return instance;
  }


  // Relationship manager for 'item' - Python ORM style (cached)
  get item(): RelationshipManager<this, Item> {
    return this.getOrCreateRelationshipManager('item', () => {
      return new RelationshipManager(this, 'item', 'one', ActionParameterTypedBy as typeof Relationship);
    });
  }

  // Relationship manager for 'bound_to' - Python ORM style (cached)
  get bound_to(): RelationshipManager<this, Action> {
    return this.getOrCreateRelationshipManager('bound_to', () => {
      return new RelationshipManager(this, 'bound_to', 'many', ActionBindsParameter as typeof Relationship);
    });
  }

  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'attributes' - Python ORM style (cached)
  get attributes(): RelationshipManager<this, Attribute> {
    return this.getOrCreateRelationshipManager('attributes', () => {
      return new RelationshipManager(this, 'attributes', 'many', HasAttribute as typeof Relationship);
    });
  }

  // Relationship manager for 'attribute_values' - Python ORM style (cached)
  get attribute_values(): RelationshipManager<this, AttributeValue> {
    return this.getOrCreateRelationshipManager('attribute_values', () => {
      return new RelationshipManager(this, 'attribute_values', 'many', HasAttributeValue as typeof Relationship);
    });
  }

  // Relationship manager for 'derived_values' - Python ORM style (cached)
  get derived_values(): RelationshipManager<this, DerivedValue> {
    return this.getOrCreateRelationshipManager('derived_values', () => {
      return new RelationshipManager(this, 'derived_values', 'many', HasDerivedValue as typeof Relationship);
    });
  }

  // Relationship manager for 'mentioned_in_expressions' - Python ORM style (cached)
  get mentioned_in_expressions(): RelationshipManager<this, Expression> {
    return this.getOrCreateRelationshipManager('mentioned_in_expressions', () => {
      return new RelationshipManager(this, 'mentioned_in_expressions', 'many', Mentions as typeof Relationship);
    });
  }

}
